import type { PlanProjection, PlanProjectionGroup } from '@developerz/domain';
import { createMemo, For, type JSX, Show } from 'solid-js';
import { t, tPlural } from '../i18n';
import { legsByRepo, legsLive, legWaits, releasedUpstreams } from '../lib/plan-dag';
import { Panel } from './Panel';
import { PlanDagRail } from './PlanDagRail';
import { PlanGoalPill } from './PlanGoalPill';
import { PlanSliceRow, type SliceDependency } from './PlanSliceRow';

// The epic read-model (#511), and since plan 101 slice 12 the GOAL: a header (plan id +
// slice count), a goal line (where the goal stands, how many repos, "N of M legs live"),
// the DAG rail at 2+ legs, then the legs GROUPED BY REPO — a heading per repo, in the order
// its first leg appears in the plan, each repo's legs in plan order (`legsByRepo`).
//
// Edges carry sibling GROUP ids, so the panel resolves each leg's upstreams against the
// projection once (group id → that group's PR number or title) — rows never render a raw
// group id. A RELEASED edge is a "Depends on" fact; an unreleased one is a wait
// (`legWaits`), so no edge is said twice.

export type FleetEpicPanelProps = {
  readonly projection: PlanProjection;
};

export function FleetEpicPanel(props: FleetEpicPanelProps): JSX.Element {
  const groups = (): readonly PlanProjectionGroup[] => props.projection.groups;
  // Built once per projection, not once per row (was an O(n^2) rebuild across the slice list).
  const byId = createMemo(() => new Map(groups().map((g) => [g.groupId, g])));
  const sections = createMemo(() => legsByRepo(groups()));
  const live = createMemo(() => legsLive(groups()));

  const resolveDeps = (group: PlanProjectionGroup): SliceDependency[] => {
    const siblings = byId();
    return releasedUpstreams(group).map((groupId) => {
      const upstream = siblings.get(groupId);
      return {
        groupId,
        title: upstream?.title ?? groupId,
        pullRequestNumber: upstream?.pullRequest?.number ?? null,
      };
    });
  };

  return (
    <Panel
      title={t('fleet.epic.title')}
      action={
        <span class="epic__id">
          <span>
            {t('fleet.epic.plan_label')} <code>{props.projection.planId}</code>
          </span>
          <span>{tPlural('fleet.epic.slices', groups().length, { count: groups().length })}</span>
        </span>
      }
    >
      <p class="epic__goal">
        <PlanGoalPill goalState={props.projection.goalState} />
        <span>
          {tPlural('fleet.epic.goal.repos', sections().length, { count: sections().length })}
        </span>
        <span>
          {tPlural('fleet.epic.goal.legs_live', live().total, {
            live: live().live,
            total: live().total,
          })}
        </span>
      </p>
      <Show when={groups().length >= 2}>
        <PlanDagRail groups={groups()} />
      </Show>
      <For each={sections()}>
        {(section) => (
          <section class="epic__repo">
            <h3 class="epic__repo-name">{section.repo}</h3>
            <ul class="epic__slices">
              <For each={section.legs}>
                {(group) => (
                  <PlanSliceRow
                    group={group}
                    total={groups().length}
                    dependsOn={resolveDeps(group)}
                    waits={legWaits(group, byId())}
                  />
                )}
              </For>
            </ul>
          </section>
        )}
      </For>
    </Panel>
  );
}
